/**
 * ROCKY-VIEW — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 3 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_ROCKY_VIEW: Location[] = [
// ─────────────────────────────────────────────────────── CHURCH RANCHES ──
  {
    slug: "church-ranches",
    name: "Church Ranches",
    region: "rocky-view",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.1333, lng: -114.3167 },
    tier: 4,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class C control zone (CYBW)",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 5,
      authorizationRequired: true,
      authorizationNote:
        "Church Ranches sits about 5 km from Springbank Airport, inside the CYBW control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Church Ranches is drawn around its water: from the air, Church Ranches Lake is the organising feature, and the acreages read as parcels arranged to face shoreline rather than street.",
      consequence:
        "Water frontage is the value here and the one thing ground photography cannot measure. At eye level the lake reads as a sliver behind the trees, so the listing ends up selling the house and omitting the reason for its price.",
      resolution:
        "A boundary-line orbit flown around the parcel keeps the shoreline in frame the whole way, showing exactly how the property meets the water — delivered in the standard turnaround.",
    },
    landmarks: [
      "Church Ranches Lake",
      "Church Ranches Boulevard",
      "Range Road 33",
      "Glendale Golf & Country Club",
      "Bearspaw Christian School",
    ],
    terrain:
      "Rolling, well-treed acreage country where large parcels wrap around open water; pasture and paddocks run out toward Range Road 33, and from altitude the community reads as countryside that happens to hold estates.",
    windNote:
      "Open chinook-belt upland. West wind arrives with little between the foothills and these parcels to slow it, and on marginal days the lake surface is the most honest wind gauge on site.",
    seasonalWindow:
      "The open-water months carry the strongest frames — between ice-off and freeze-up the lake doubles a calm evening in reflection. After freeze-up the water reads as a white field, and the shot list shifts to treelines and parcel boundaries.",
    bylawNote:
      "Rocky View County's rules govern takeoff and landing on municipal land, so launches are staged from the subject property with the owner's permission.",
    subjectMix: ["acreage-estate", "luxury-residential", "agricultural"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. The CYBW authorisation is filed as part of booking, and a Church Ranches visit pairs naturally with Bearspaw, Watermark or Silverhorn on a single mobilisation.",
    faqs: [
      {
        question: "Do drone flights at Church Ranches need airspace authorisation?",
        answer:
          "Yes. Church Ranches sits about 5 km from Springbank Airport, inside the CYBW control zone as charted. Flights are conducted under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot date is confirmed, and the exact site and altitude are checked against NAV Drone before every flight.",
      },
      {
        question: "How do you photograph a lakefront parcel at Church Ranches?",
        answer:
          "From altitude, with the shoreline as the organising line. A slow orbit flown along the parcel boundary shows how much frontage the property actually commands on Church Ranches Lake, where the house sits relative to the water, and how the treed edges frame it — none of which survives a ground-level photograph.",
      },
      {
        question: "When is the best light for a Church Ranches shoot?",
        answer:
          "Calm light beats dramatic light here. The lake is the frame's anchor, and a glassy surface at golden hour doubles the property in reflection. Wind is typically lightest early in the day before daytime heating builds, so morning holds are scheduled when a mirror-flat lake matters to the shot list.",
      },
    ],
    primaryKeywords: [
      "drone photography Church Ranches",
      "aerial photography Church Ranches",
      "Church Ranches drone services",
    ],
    nearestLocations: ["bearspaw", "watermark", "silverhorn", "cochrane"],
  },

  // ─────────────────────────────────────────────────────────── SILVERHORN ──
  {
    slug: "silverhorn",
    name: "Silverhorn",
    region: "rocky-view",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.1167, lng: -114.3 },
    tier: 4,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 20,
      authorizationRequired: true,
      authorizationNote:
        "At about 20 km from Calgary International, Silverhorn runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Irrigated turf keeps its own calendar: the fairways of Glendale Golf & Country Club green up ahead of the surrounding grassland in spring and hold their colour after it cures to brown, so from the air the season around Silverhorn is legible at a glance.",
      consequence:
        "A listing shot a few weeks late sets an emerald lawn inside a cured brown frame, and the contrast a buyer notices is not the one the seller intended. From the ground the change is easy to miss; from altitude it dominates the composition.",
      resolution:
        "The date is set against the ground rather than the calendar — flown in the weeks when lawn, shelterbelt and grassland agree — as an approach run that resolves into a slow rise over the house, delivered in the standard turnaround.",
    },
    landmarks: [
      "Silverhorn Drive",
      "Bearspaw Road",
      "Glendale Golf & Country Club",
      "Bearspaw Lifestyle Centre",
    ],
    terrain:
      "Gently rolling estate country on the Bearspaw upland: large manicured parcels, young plantings set against older shelterbelts, and open grassland running between them, so the land reads from the air as a patchwork of managed and native ground.",
    windNote:
      "Chinook belt, open upland exposure. West wind crosses this ground with little to break it, and the gust spread — not the average speed — decides whether a stable hold is flyable.",
    seasonalWindow:
      "Spring green-up and the autumn turn are the two strongest windows: young plantings and mature belts change on different schedules, and the weeks when they align flatter an estate most. Midwinter flattens the palette, though snow makes parcel boundaries read cleanly.",
    bylawNote:
      "Municipal land here falls under Rocky View County park and roadway rules; the default is to launch from the subject property with the owner's consent.",
    subjectMix: ["acreage-estate", "luxury-residential"],
    mobilisationNote:
      "About 25 minutes from the Calgary base via Bearspaw Road. Same-day work is realistic once weather and authorisation align, and Watermark, Bearspaw and Church Ranches sit minutes away for a combined booking.",
    faqs: [
      {
        question: "Does flying at Silverhorn require NAV CANADA authorisation?",
        answer:
          "Treat it as required. Silverhorn sits about 20 km from Calgary International, near the charted boundary of the CYYC control zone — close enough that which side of the line an address falls on decides the paperwork. Each site is checked against NAV Drone, and any required authorisation is filed before the date is booked.",
      },
      {
        question: "What time of year photographs a Silverhorn estate best?",
        answer:
          "The shoulder weeks. In spring the irrigated lawns and fairways green up before the native grassland; in autumn the shelterbelts turn while the turf holds colour. Either window gives an estate contrast against its surroundings. Midsummer can render everything a single green, and midwinter trades colour for clean boundary lines.",
      },
      {
        question: "Can a Silverhorn shoot be combined with a neighbouring community?",
        answer:
          "Yes. Silverhorn sits about 25 minutes from the Calgary base, within minutes of Watermark at Bearspaw and Church Ranches, and airspace planning is handled the same way across the area — checked against NAV Drone and filed before booking. Photographing two or more properties on one mobilisation is routine and usually the most efficient way to schedule.",
      },
    ],
    primaryKeywords: [
      "drone photography Silverhorn",
      "aerial photography Silverhorn",
      "Silverhorn drone services",
    ],
    nearestLocations: ["watermark", "bearspaw", "church-ranches", "cochrane"],
  },

  // ──────────────────────────────────────────────── WATERMARK AT BEARSPAW ──
  {
    slug: "watermark",
    name: "Watermark at Bearspaw",
    region: "rocky-view",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.1297, lng: -114.275 },
    tier: 4,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 18,
      authorizationRequired: true,
      authorizationNote:
        "Watermark at Bearspaw is about 18 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Watermark occupies open upland with an unbroken western horizon — along Watermark Boulevard and Watermark Heights nothing stands tall enough to cut the low sun, so golden hour here runs to the very end of the day instead of dying early behind terrain.",
      consequence:
        "Book that property for a mid-day slot and the light works against it: overhead sun flattens rooflines and stone, and the hour that justifies the build never appears in the gallery. From the ground there is no way to hold a whole estate in that late light anyway.",
      resolution:
        "A golden-hour hold puts the aircraft on station for the last usable light and keeps it there, so the frames are made in the property's best hour — delivered in the standard turnaround.",
    },
    landmarks: [
      "Watermark Boulevard",
      "Watermark Heights",
      "Glendale Golf & Country Club",
      "Bearspaw Lifestyle Centre",
      "Bearspaw Christian School",
    ],
    terrain:
      "Open upland at the eastern edge of Bearspaw country: large formal estate lots where managed lawns meet native grassland, with long sightlines in every direction and the city visible to the east from altitude.",
    windNote:
      "Chinook-belt ground with full western exposure. When a chinook arch builds, surface wind can rise quickly here, and the open horizon that flatters the light also gives the wind a clean run.",
    seasonalWindow:
      "Golden hour swings widely across the year at this latitude — a winter shoot works the mid-afternoon, a June shoot waits for late evening. The open western exposure makes the last hour of sun dependable in every season; it is wind, not light, that usually sets the date.",
    bylawNote:
      "On municipal land, Rocky View County's rules apply to drone takeoff and landing; flights are flown from private property with the owner's permission as standard.",
    subjectMix: ["luxury-residential", "acreage-estate"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. The airspace answer is settled at booking, and neighbouring Bearspaw, Silverhorn and Church Ranches are close enough to photograph on the same trip.",
    faqs: [
      {
        question: "What airspace rules apply to a Watermark at Bearspaw shoot?",
        answer:
          "It is planned as controlled airspace. Watermark sits about 18 km from Calgary International, near the charted boundary of the CYYC control zone, and is treated as controlled until NAV Drone shows otherwise for the specific site. Where authorisation is required it is filed before the date is booked.",
      },
      {
        question: "When should a Watermark at Bearspaw shoot be scheduled?",
        answer:
          "Against the sun, not the diary. The community's open western horizon gives it a long, dependable golden hour, and that low raking light is what separates a luxury elevation from a flat mid-day record shot. The hold is scheduled for the last usable hour and the date flexes around wind.",
      },
      {
        question: "Why does Watermark's airspace planning differ from Church Ranches next door?",
        answer:
          "Different zones govern them. Church Ranches is planned against Springbank Airport's control zone, while Watermark sits about 18 km from Calgary International, near the charted CYYC boundary. The difference changes the paperwork, not the outcome: each site is checked against NAV Drone and any required authorisation is filed before booking.",
      },
    ],
    primaryKeywords: [
      "drone photography Watermark at Bearspaw",
      "aerial photography Watermark at Bearspaw",
      "Watermark at Bearspaw drone services",
    ],
    nearestLocations: ["bearspaw", "silverhorn", "church-ranches", "cochrane"],
  }
];
